import axios from "axios";
import { API_BASE } from "./_ApiBase";
import { APP_STORAGE } from "../../_RuntimeConfig/_Config/Acomodar/AppConfig.Storage.js";
import { API_LOAD_ENDPOINTS } from "../ApiEndpoints.js";
import { AUTH_CONSTANTS } from "../ApiEndpoints.js";
import { STORAGE_SESSION_HELPER } from "../../../_Helpers/Helpers/storageSession.helper.js";
import { APP_ENVIRONMENT } from "../../_RuntimeConfig/_Config/Acomodar/AppEnvironment.js";


export const API_BASE_URL = API_BASE.API_URL;
export const API_STATIC_URL = API_BASE.STATIC_URL;
export const API_SOCKET_DOMAIN = API_BASE.SOCKET_URL;

const AUTO_LOGOUT_ON_401 = APP_ENVIRONMENT.AUTH.LOGOUT_ON_401;


// 🔥 KEY vía constante → URL vía registro de endpoints (sin strings crudos)
const REFRESH_TOKEN_URL = AUTH_CONSTANTS.REFRESH_TOKEN ? API_LOAD_ENDPOINTS[AUTH_CONSTANTS.REFRESH_TOKEN] : null;


// Agregamos withCredentials de manera global para que las cookies. (como 'refreshToken') viajen automáticamente en cada petición hacia el backend.
const AXIOS_API_CREATE = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 30000,
    headers: { Accept: 'application/json' },
});


// ------------------------------------------ 🔥 REQUEST INTERCEPTOR 
AXIOS_API_CREATE.interceptors.request.use((config) => {
    const token = STORAGE_SESSION_HELPER.GET_AUTH_STORAGE(APP_STORAGE.TOKEN_USER_AUTH);
    const browserCrypto = typeof window !== 'undefined' ? window.crypto : null;
    const requestId = browserCrypto && typeof browserCrypto.randomUUID === 'function'
        ? browserCrypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    if (config.headers && typeof config.headers.set === 'function') {
        if (!config.headers.get?.('X-Request-Id')) config.headers.set('X-Request-Id', requestId);
    } else {
        config.headers = config.headers || {};
        if (!config.headers['X-Request-Id']) config.headers['X-Request-Id'] = requestId;
    }
    const isRefreshRequest = REFRESH_TOKEN_URL ? config?.url?.includes(REFRESH_TOKEN_URL) : false;

    if (token && !isRefreshRequest) {
        if (config.headers && typeof config.headers.set === 'function') {
            config.headers.set('Authorization', `Bearer ${token}`);
        } else {
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return config;
}, (error) => Promise.reject(error));


// ------------------------------------------ 🔥 RESPONSE INTERCEPTOR
let isRefreshing = false;
let failedQueue = [];


const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};


AXIOS_API_CREATE.interceptors.response.use((response) => response, async (error) => {
    if (!error) return Promise.reject(new Error("Unknown Error"));

    const OPT_REQUEST = error.config;
    if (!OPT_REQUEST) return Promise.reject(error);

    const STATUS = error.response?.status;
    const ERROR_CODE = error.response?.data?.data?.code || error.response?.data?.code;

    if (STATUS === 401 && ERROR_CODE === "TOKEN_EXPIRED" && !OPT_REQUEST._retry) {

        if (!REFRESH_TOKEN_URL) {
            if (AUTO_LOGOUT_ON_401) STORAGE_SESSION_HELPER.CLEAR_AUTH();
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                OPT_REQUEST.headers = { ...(OPT_REQUEST.headers || {}), Authorization: `Bearer ${token}` };
                return AXIOS_API_CREATE(OPT_REQUEST);
            }).catch(err => Promise.reject(err));
        }

        OPT_REQUEST._retry = true;
        isRefreshing = true;

        try {
            // El navegador inyectará la cookie automáticamente gracias a 'withCredentials: true'.
            const REFRESH_AXIOS_CREATE = axios.create({
                baseURL: API_BASE_URL, withCredentials: true, timeout: 30000 // para enviar la cookie actual y recibir la nueva rotada
            });

            // objeto vacío '{}' en el body porque los datos van en la cookie
            const REFRESH_RESPONSE = await REFRESH_AXIOS_CREATE.post(
                REFRESH_TOKEN_URL, {}, { headers: { Authorization: undefined } }
            );

            const NEW_TOKEN = REFRESH_RESPONSE.data?.data?.accessToken;

            if (!NEW_TOKEN) {
                throw new Error("El servidor respondió 200 pero el accessToken vino vacío en el JSON");
            }

            STORAGE_SESSION_HELPER.SET_AUTH_STORAGE(APP_STORAGE.TOKEN_USER_AUTH, NEW_TOKEN);

            processQueue(null, NEW_TOKEN);

            OPT_REQUEST.headers = {
                ...(OPT_REQUEST.headers || {}), Authorization: `Bearer ${NEW_TOKEN}`
            };

            return AXIOS_API_CREATE(OPT_REQUEST);

        } catch (refreshError) {
            processQueue(refreshError, null);

            if (refreshError.response?.status === 401 || refreshError.response?.status === 403 || refreshError.message.includes("Storage")) {

                if (AUTO_LOGOUT_ON_401) STORAGE_SESSION_HELPER.CLEAR_AUTH();

                if (typeof window !== "undefined") {
                    // window.location.href = "/auth/login";
                }
            }

            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }

    return Promise.reject(error);
}
);

export default AXIOS_API_CREATE;